import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import get from 'lodash/get';
import flatten from 'lodash/flatten';
import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';
import surveyUrls from './surveyUrls';
import FhirPropertyParser from '../../../util/fhirResourceParsers/FhirPropertyParser';
import {
  FhirBoolean,
  Coding,
  Date as FhirDate,
  FhirDateTime,
  Decimal,
  Integer,
  Quantity,
  FhirString,
  Time,
  URI,
  FhirArray,
} from '../../../util/fhirResourceParsers/Types';
import ExtensionCollection from './ExtensionCollection';

export type ResponseValueTypes =
  | 'valueBoolean'
  | 'valueDecimal'
  | 'valueInteger'
  | 'valueDate'
  | 'valueDateTime'
  | 'valueTime'
  | 'valueString'
  | 'valueUri'
  | 'valueAttachment'
  | 'valueCoding'
  | 'valueQuantity';

const copyAndShift = (source: any[] = []) => {
  const copied = [...source];
  copied.shift();
  return copied;
};

type FhirResponseWithoutItem = Omit<fhir.QuestionnaireResponse, 'item'>;
type FhirResponseItemWithoutItem = Pick<
  fhir.QuestionnaireResponseItem,
  Exclude<keyof fhir.QuestionnaireResponseItem, 'item'>
>;

export class ResponseUpdate {
  _updatePath: string[];
  _valueType: ResponseValueTypes;
  _value: any;

  constructor(updatePath: string[], valueType: ResponseValueTypes, value: any) {
    this._updatePath = updatePath;
    this._valueType = valueType;
    this._value = value;
  }

  get updatePath(): string[] {
    return this._updatePath;
  }

  get valueType(): ResponseValueTypes {
    return this._valueType;
  }

  get value(): any {
    return this._value;
  }
}

export type ParentResponseItemSubItems = { [link: string]: ResponseItem };
export type LeafSubItems = 'no-sub-items';
export type ResponseItemSubItems = ParentResponseItemSubItems | LeafSubItems;

export class ResponseItem {
  static initEmptyResponseItem(
    linkId: string,
    valueType: ResponseValueTypes,
  ): ResponseItem {
    return new ResponseItem(linkId, 'no-sub-items', valueType);
  }

  /**
   * Initialize a response item and its children items from the item backbone element of a fhir resource
   */
  static initFromFhirResponseItem(
    fhirResponseItem: fhir.QuestionnaireResponseItem,
    repeatingSurveyItems: string[] = [],
  ): ResponseItem {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!fhirResponseItem || !isObject(fhirResponseItem)) return null;

    const {
      linkId,
      item,
      answer = [] as fhir.QuestionnaireResponseItemAnswer[],
    } = fhirResponseItem;

    // linkid requeired
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!linkId) return null;

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    let valueType: ResponseValueTypes = null;
    const arrayValue: any[] = [];

    answer.forEach(
      (singleAnswer: fhir.QuestionnaireResponseItemAnswer = {}) => {
        const {
          valueBoolean,
          valueDecimal,
          valueInteger,
          valueDate,
          valueDateTime,
          valueTime,
          valueString,
          valueUri,
          valueCoding,
          valueQuantity,
          valueAttachment,
        } = singleAnswer;

        if (valueBoolean !== undefined) {
          valueType = 'valueBoolean';
          arrayValue.push(valueBoolean);
        } else if (valueDecimal !== undefined) {
          valueType = 'valueDecimal';
          arrayValue.push(valueDecimal);
        } else if (valueInteger !== undefined) {
          valueType = 'valueInteger';
          arrayValue.push(valueInteger);
        } else if (valueDate !== undefined) {
          valueType = 'valueDate';
          arrayValue.push(valueDate);
        } else if (valueDateTime !== undefined) {
          valueType = 'valueDateTime';
          arrayValue.push(valueDateTime);
        } else if (valueTime !== undefined) {
          valueType = 'valueTime';
          arrayValue.push(valueTime);
        } else if (valueString !== undefined) {
          valueType = 'valueString';
          arrayValue.push(valueString);
        } else if (valueUri !== undefined) {
          valueType = 'valueUri';
          arrayValue.push(valueUri);
        } else if (valueCoding !== undefined) {
          valueType = 'valueCoding';
          arrayValue.push(valueCoding);
        } else if (valueQuantity !== undefined) {
          valueType = 'valueQuantity';
          arrayValue.push(valueQuantity);
        } else if (valueAttachment !== undefined) {
          valueType = 'valueAttachment';
          arrayValue.push(valueAttachment);
        }
      },
    );

    const repeats = repeatingSurveyItems.includes(linkId);

    if (arrayValue.length > 1 && !repeats) {
      console.error(
        'Received a questionnaire response with a repeating value on a non-repeating survey item.  Extra values will be ignored',
      );
    }

    const value = repeats ? arrayValue : arrayValue[0];

    // recursively initialize child response items
    const subItems: { [id: string]: ResponseItem } = {};
    if (item && isArray(item)) {
      item
        .map(subItem =>
          ResponseItem.initFromFhirResponseItem(subItem, repeatingSurveyItems),
        )
        .filter(Boolean)
        .forEach(childResponseItem => {
          subItems[childResponseItem.link] = childResponseItem;
        });
    }

    const { item: ommittedItem, ...responseItemWithoutSubItems } =
      fhirResponseItem;
    return new ResponseItem(
      linkId,
      subItems,
      valueType,
      value,
      responseItemWithoutSubItems,
    );
  }

  link: string;
  valueType: ResponseValueTypes;
  value: any;
  subItems: ResponseItemSubItems;
  restOfItemData: FhirResponseItemWithoutItem;

  constructor(
    link: string,
    subItems: ResponseItemSubItems,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    valueType: ResponseValueTypes = null,
    value?: any,
    restOfItemData?: FhirResponseItemWithoutItem,
  ) {
    this.link = link;
    this.valueType = valueType;
    this.value = isNil(value) ? null : value;
    this.subItems = subItems;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this.restOfItemData = restOfItemData;
  }

  /**
   * Does this response item represent a grouping as opposed to a value
   *
   * @readonly
   * @memberof ResponseItem
   */
  get isGroupResponseItem(): boolean {
    return this.valueType === null;
  }

  get hasResponse(): boolean {
    return this.isArrayValue
      ? this.value !== null && this.value && this.value.length > 0
      : this.value !== null;
  }

  get isArrayValue(): boolean {
    return isArray(this.value);
  }

  get subItemlist() {
    return Object.values(this._safeParentSubItems);
  }

  /**
   * All response items nested within the tree
   */
  get allTreeResponseItems(): ResponseItem[] {
    return flatten([
      this,
      ...this.subItemlist.map(_ => _.allTreeResponseItems),
    ]);
  }

  private get _safeParentSubItems(): ParentResponseItemSubItems {
    return this.subItems === 'no-sub-items' ? {} : this.subItems || {};
  }

  getSubItemWithLink(linkId: string) {
    return this._safeParentSubItems[linkId] || null;
  }

  setSubItemWithLink(linkId: string, subItem: ResponseItem): void {
    if (this.subItems === 'no-sub-items')
      throw new Error(
        'Attempted to set a response sub item on a leaf survey response item',
      );

    this.subItems[linkId] = subItem;
  }

  removeSubItemWithLink(linkId: string): void {
    if (this.subItems !== 'no-sub-items') {
      delete this.subItems[linkId];
    }
  }

  /**
   * null values are not valid fhir.  Response items with null values should be filtered out
   */
  get isNullValuedLeafItem() {
    return this.subItems === 'no-sub-items' && this.value === null;
  }

  private _valueAsArray(): any[] {
    if (this.value === null) return [];

    return isArray(this.value) ? this.value : [this.value];
  }

  toFhirResponseItem(): fhir.QuestionnaireResponseItem {
    const subItems = Object.keys(this._safeParentSubItems)
      .map(subItemLink => {
        const subItem = this.getSubItemWithLink(subItemLink);

        if (!subItem) return null;

        return subItem.toFhirResponseItem();
      })
      .filter(Boolean);

    const item = {
      ...this.restOfItemData,
      linkId: this.link,
    } as fhir.QuestionnaireResponseItem;

    if (subItems.length > 0) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      item.item = subItems;
    }

    if (this.valueType) {
      item.answer = this._valueAsArray().map(value => ({
        [this.valueType]: value,
      }));
    }

    return item;
  }

  get valueDisplay(): string {
    if (this.value === null) return '';

    const fhirResponseItem = this.toFhirResponseItem();

    const rawDisplayValue =
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      new FhirPropertyParser('resource.answer', FhirArray, {
        fhirArray: {
          optionalPathParsers: [
            {
              arrayElementPath: 'valueBoolean',
              arrayElementParser: new FhirBoolean(),
            },
            {
              arrayElementPath: 'valueCoding',
              arrayElementParser: new Coding(),
            },
            {
              arrayElementPath: 'valueDate',
              arrayElementParser: new FhirDate(),
            },
            {
              arrayElementPath: 'valueDateTime',
              arrayElementParser: new FhirDateTime(),
            },
            {
              arrayElementPath: 'valueDecimal',
              arrayElementParser: new Decimal(),
            },
            {
              arrayElementPath: 'valueInteger',
              arrayElementParser: new Integer(),
            },
            {
              arrayElementPath: 'valueQuantity',
              arrayElementParser: new Quantity(),
            },
            {
              arrayElementPath: 'valueString',
              arrayElementParser: new FhirString(),
            },
            {
              arrayElementPath: 'valueTime',
              arrayElementParser: new Time(),
            },
            {
              arrayElementPath: 'URI',
              arrayElementParser: new URI(),
            },
          ],
          concatenate: true,
        },
      }).getDisplayValue({ resource: fhirResponseItem }) || '';

    return String(rawDisplayValue);
  }
}

/**
 * An immutable class for storing response state and
 *
 * @export
 * @class SurveyResponse
 */
export default class SurveyResponse {
  static statuses = {
    inProgress: 'in-progress',
    completed: 'completed',
    amended: 'amended',
    enteredInError: 'entered-in-error',
    stopped: 'stopped',
  };

  /**
   * Initializes a survey response from a fhir questionnaire
   * @param {Object} fhirQuestionnaireResponse - A raw fhir questionnaire response
   * @param {String[]} repeatingSurveyItems - A list of repeating survey items.  This will determine if a item's value will be an array or non-array.
   */
  static initFromFhirQuestionnaireResponse(
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    fhirQuestionnaireResponse: fhir.QuestionnaireResponse = null,
    repeatingSurveyItems: string[] = [],
  ): SurveyResponse {
    if (!fhirQuestionnaireResponse || !isObject(fhirQuestionnaireResponse)) {
      return new SurveyResponse();
    }

    const { item } = fhirQuestionnaireResponse;

    const extensions = get<
      fhir.QuestionnaireResponse,
      'extension',
      fhir.Extension[]
    >(fhirQuestionnaireResponse, 'extension', [] as fhir.Extension[]);
    const lastVisitedPageExtension = extensions.find(
      (extension: fhir.Extension = {} as fhir.Extension) =>
        extension.url === surveyUrls.lastVisitedPage,
    );

    const lastVisitedPage = get<fhir.Extension, 'valueString', string>(
      lastVisitedPageExtension,
      'valueString',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );

    if (!item || !isArray(item)) {
      return new SurveyResponse({}, lastVisitedPage, fhirQuestionnaireResponse);
    }

    const responseItems: { [id: string]: ResponseItem } = {};
    item
      .map(fhirResponseItem =>
        ResponseItem.initFromFhirResponseItem(
          fhirResponseItem,
          repeatingSurveyItems,
        ),
      )
      .filter(Boolean)
      .forEach(responseItem => {
        responseItems[responseItem.link] = responseItem;
      });

    return new SurveyResponse(
      responseItems,
      lastVisitedPage,
      fhirQuestionnaireResponse,
    );
  }

  /**
   * Determines if the given fhir questionnaire response is empty and not even partially completed by the user
   * @param {Object} fhirQuestionnaireResponse - A raw fhir questionnaire response
   */
  static isQuestionnaireResponseNotStarted(
    fhirQuestionnaireResponse: fhir.QuestionnaireResponse,
  ): boolean {
    if (!fhirQuestionnaireResponse) return true;

    const extensions = get<
      fhir.QuestionnaireResponse,
      'extension',
      fhir.Extension[]
    >(fhirQuestionnaireResponse, 'extension', [] as fhir.Extension[]);
    return extensions.some(
      (extension: fhir.Extension = {} as fhir.Extension) =>
        extension.url === surveyUrls.surveyNotStarted,
    );
  }

  /**
   * Gets the source questionnaire ID of the fhir questionnaire response
   * @param fhirQuestionnaireResponse - A raw fhir questionnaire response
   */
  static getSourceQuestionnaire(
    fhirQuestionnaireResponse: fhir.QuestionnaireResponse,
  ): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return fhirQuestionnaireResponse?.questionnaire?.reference
      ?.split('/')
      ?.at(-1);
  }

  /**
   * Gets the primary survey ID of the fhir questionnaire response
   * @param fhirQuestionnaireResponse - A raw fhir questionnaire response
   */
  static getPrimarySurveyId(
    fhirQuestionnaireResponse: fhir.QuestionnaireResponse,
  ): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return fhirQuestionnaireResponse?.meta.tag.find(
      _ => _?.system === surveyUrls.primarySurveyId,
    ).code;
  }

  responseItems: { [rootLink: string]: ResponseItem };
  lastVisitedPage: string;
  responseWithoutItem: FhirResponseWithoutItem;

  constructor(
    responseItems: { [rootLink: string]: ResponseItem } = {},
    lastVisitedPage?: string,
    responseWithoutItem?: FhirResponseWithoutItem,
  ) {
    this.responseItems = responseItems;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this.lastVisitedPage = lastVisitedPage || null;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this.responseWithoutItem = responseWithoutItem;
  }

  get isComplete() {
    const currentStatus = get(this.responseWithoutItem, 'status');
    return currentStatus === SurveyResponse.statuses.completed;
  }

  get isInProgress() {
    if (this.isComplete) return false;

    const extensionCollection = ExtensionCollection.initFromBackboneElement(
      this.responseWithoutItem,
    );
    return !extensionCollection.hasExtension(surveyUrls.surveyNotStarted);
  }

  get id() {
    return get(this.responseWithoutItem, 'id');
  }

  _clone(): SurveyResponse {
    // faster implementation possible, but for right now this should work
    const clonedItems = cloneDeep(this.responseItems);
    return new SurveyResponse(
      clonedItems,
      this.lastVisitedPage,
      this.responseWithoutItem,
    );
  }

  _recursiveGetItemAtPath(
    path: string[] = [],
    responseItem: ResponseItem,
  ): ResponseItem {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!responseItem) return null;

    if (path.length === 0) {
      return responseItem;
    } else {
      const itemLink = path[0];
      const childItem = responseItem.getSubItemWithLink(itemLink);

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      if (!childItem) return null;

      const copiedPath = [...path];
      copiedPath.shift();
      return this._recursiveGetItemAtPath(copiedPath, childItem);
    }
  }

  _getAllResponseItems(): ResponseItem[] {
    const recursiveGetItems = (responseItem: ResponseItem): ResponseItem[] => {
      if (responseItem.isGroupResponseItem) {
        return flatten([
          responseItem,
          ...(responseItem.subItemlist || []).map(_ => recursiveGetItems(_)),
        ]);
      } else {
        return [responseItem];
      }
    };

    return flatten(
      Object.values(this.responseItems || {}).map(_ => recursiveGetItems(_)),
    );
  }

  get allResponseItems() {
    return this._getAllResponseItems();
  }

  get rootResponseItems() {
    return Object.values(this.responseItems);
  }

  get allAttachmentUrls(): string[] {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return this.allResponseItems
      .filter(_ => _.valueType === 'valueAttachment')
      .map(_ => (_.value as fhir.Attachment)?.url)
      .filter(Boolean);
  }

  get questionnaireName(): string {
    return this.responseWithoutItem?.questionnaire?.display || '';
  }

  get authoredDate(): Date | null {
    if (!this.responseWithoutItem?.authored) return null;

    const date = new Date(this.responseWithoutItem.authored);
    return isNaN(date.getTime()) ? null : date;
  }

  getValueAtPath(path: string[] = []): any {
    const targetRootLink = path[0];
    const targetRoot = this.responseItems[targetRootLink];
    const copiedPath = copyAndShift(path);
    const targetItem = this._recursiveGetItemAtPath(copiedPath, targetRoot);

    return targetItem === null ? null : targetItem.value;
  }

  /**
   * Gets the value of the specified linkId.  Note that this is not as performant as getValueAtPath
   * and should likely only be used for testing
   */
  getValue(linkId: string): any {
    const targetItem = this.allResponseItems.find(_ => _.link === linkId);
    return isNil(targetItem?.value) ? null : targetItem?.value;
  }

  getItemAtPath(path: string[] = []): ResponseItem {
    const targetRootLink = path[0];
    const targetRoot = this.responseItems[targetRootLink];
    const copiedPath = copyAndShift(path);
    return this._recursiveGetItemAtPath(copiedPath, targetRoot);
  }

  updateFields(updates: ResponseUpdate[]): SurveyResponse {
    const recursiveUpdate = (
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      sourceItem: ResponseItem = null,
      path: string[],
      valueType: ResponseValueTypes,
      value: any,
    ): ResponseItem => {
      if (path.length === 1) {
        return new ResponseItem(path[0], 'no-sub-items', valueType, value);
      }

      const link = path[0];
      const childPath = copyAndShift(path);
      const childLink = childPath[0];
      const childItem = sourceItem
        ? sourceItem.getSubItemWithLink(childLink)
        : null;
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const newChild = recursiveUpdate(childItem, childPath, valueType, value);

      // if response item at this level has not been instantiated
      if (!sourceItem) {
        const subItems: ParentResponseItemSubItems = {};

        if (!newChild.isNullValuedLeafItem) {
          subItems[childLink] = newChild;
        }

        return new ResponseItem(link, subItems);
      } else if (!newChild.isNullValuedLeafItem) {
        sourceItem.setSubItemWithLink(childLink, newChild);
        return sourceItem;
      } else {
        sourceItem.removeSubItemWithLink(childLink);
        return sourceItem;
      }
    };

    const clonedSurveyResponse = this._clone();
    updates.forEach(update => {
      const rootItemLink = update.updatePath[0];
      const rootItem = clonedSurveyResponse.responseItems[rootItemLink];
      const updatedRoot = recursiveUpdate(
        rootItem,
        update.updatePath,
        update.valueType,
        update.value,
      );
      clonedSurveyResponse.responseItems[rootItemLink] = updatedRoot;
    });

    return clonedSurveyResponse;
  }

  /**
   * Returns the initial form value for the given root link.
   * The initial form value consists of all the leaf values of the form tree.
   *
   * @param {string} [rootLink=null] - The root page link.  If null, will get the whole form's value and not the paginated value
   * @returns {Object}
   * @memberof SurveyResponse
   */
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  getDefaultFormValues(rootLink: string = null): Record<string, any> {
    const recursiveGetLeafValues = (
      responseItem: ResponseItem,
      currentValue: Record<string, any> = {},
    ) => {
      if (responseItem.isGroupResponseItem) {
        let newCurValue = { ...currentValue };
        Object.keys(responseItem.subItems).forEach(subItemLink => {
          const subItem = responseItem.getSubItemWithLink(subItemLink);

          newCurValue = recursiveGetLeafValues(subItem, newCurValue);
        });

        return newCurValue;
      } else {
        return {
          ...currentValue,
          [responseItem.link]: responseItem.value,
        };
      }
    };

    const rootItems = rootLink
      ? [this.responseItems[rootLink]]
      : Object.keys(this.responseItems).map(link => this.responseItems[link]);

    let initialFormValues = {};
    rootItems.filter(Boolean).forEach(rootItem => {
      initialFormValues = recursiveGetLeafValues(rootItem, initialFormValues);
    });

    return initialFormValues;
  }

  /**
   * Returns a cloned survey response with the last visited root link.  It is useful to persists this so that a survey taker will resume at the last page they were on
   * @param {string} pageLink
   */
  updateLastVisitedPage(pageLink: string): SurveyResponse {
    return new SurveyResponse(
      this.responseItems,
      pageLink,
      this.responseWithoutItem,
    );
  }

  /**
   * Clones the survey response and marks it as complete
   *
   * @memberof SurveyResponse
   */
  setAsComplete(): SurveyResponse {
    const fhirDataWithCompleteStatus = {
      ...(this.responseWithoutItem || {}),
      status: SurveyResponse.statuses.completed,
    };

    return new SurveyResponse(
      this.responseItems,
      this.lastVisitedPage,
      fhirDataWithCompleteStatus,
    );
  }

  setAsInProgress(): SurveyResponse {
    const fhirDataWithInProgressStatus = {
      ...(this.responseWithoutItem || {}),
      status: SurveyResponse.statuses.inProgress,
    };

    return new SurveyResponse(
      this.responseItems,
      this.lastVisitedPage,
      fhirDataWithInProgressStatus,
    );
  }

  get itemsWithValues(): ResponseItem[] {
    return this._getAllResponseItems().filter(_ => _.hasResponse);
  }

  /**
   * If a survey is completed on behalf of the survey subject (ie: doctor filling out for subject) an author should be set
   */
  setAuthor(author: { reference?: string; display?: string }): SurveyResponse {
    const fhirDataWithAuthor = {
      ...(this.responseWithoutItem || ({} as FhirResponseWithoutItem)),
      author,
    };

    return new SurveyResponse(
      this.responseItems,
      this.lastVisitedPage,
      fhirDataWithAuthor,
    );
  }

  addAuthorExtension(reference: string): SurveyResponse {
    const extension = {
      url: 'https://lifeomic.com/fhir/author',
      valueString: reference,
      extension: [
        {
          url: 'https://lifeomic.com/fhir/authored',
          valueDateTime: new Date().toISOString(),
        },
      ],
    };
    const fhirDataWithAuthor = {
      ...(this.responseWithoutItem || ({} as FhirResponseWithoutItem)),
      extension: Array.isArray(this.responseWithoutItem?.extension)
        ? this.responseWithoutItem.extension.concat(extension)
        : [extension],
    };

    return new SurveyResponse(
      this.responseItems,
      this.lastVisitedPage,
      fhirDataWithAuthor,
    );
  }

  get sourceQuestionnaireId(): string | null {
    return SurveyResponse.getSourceQuestionnaire(this.responseWithoutItem);
  }

  get author(): string | null {
    return this.responseWithoutItem.author?.reference || null;
  }

  toFhirQuestionnaireResponse(): fhir.QuestionnaireResponse {
    // be sure to strip "survey not started" extension as it has atleast been updated by the user
    const { extension = [], ...nonExtensionFhirData } =
      this.responseWithoutItem || {};

    // remove not start and last visited page extensions
    const extensionsToKeep = extension.filter(
      (extension: fhir.Extension = {} as fhir.Extension) =>
        extension.url !== surveyUrls.surveyNotStarted &&
        extension.url !== surveyUrls.lastVisitedPage,
    );

    // only add extension if value exists
    if (this.lastVisitedPage) {
      extensionsToKeep.push({
        url: surveyUrls.lastVisitedPage,
        valueString: this.lastVisitedPage,
      });
    }

    const fhirResource = {
      ...nonExtensionFhirData,
      authored: this.isComplete
        ? nonExtensionFhirData.authored
        : new Date().toISOString(),
      item: Object.keys(this.responseItems)
        .map(responseItemLink => {
          const responseItem = this.responseItems[responseItemLink];

          if (!responseItem) return null;

          return responseItem.toFhirResponseItem();
        })
        .filter(Boolean),
    } as fhir.QuestionnaireResponse;

    if (extensionsToKeep.length > 0) {
      fhirResource.extension = extensionsToKeep;
    }

    return fhirResource;
  }
}
